import {Logo} from "assets";
import ChatProvider from "contexts/Chat";
import useChats from "contexts/Chat/useChats";
import {MessageCircle} from "lucide-react";
import {FC} from "react";
import {Navigate, Outlet, useLocation, useNavigate} from "react-router";
import AppHeader, {type RouteConfig} from "./AppHeader";

export type {RouteConfig};

interface Props {
  routes: RouteConfig[];
}

const UnreadBubble: FC = () => {
  const {threads} = useChats();
  const hasUnread = threads?.some((thread) => !!thread.unread);
  if (!hasUnread) return null;
  return <div className="absolute w-2 h-2 rounded-full top-0 right-0 bg-red-500" />;
};

const CommonAppLayout: FC<Props> = ({routes}) => {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  const hideSidebar = pathname.startsWith("/messages") || pathname.startsWith("/settings");

  if (pathname === "/") return <Navigate to={routes[0].path} replace />;

  return (
    <ChatProvider>
      <div className="w-full h-full flex overflow-hidden">
        {/* Sidebar */}
        {!hideSidebar && (
          <div className="h-full bg-brand-navy flex flex-col items-center py-4 flex-shrink-0 w-20 overflow-hidden">
            <Logo className="w-10 h-10 p-1.5 rounded-xl bg-brand-orange fill-white shrink-0 mb-4" />

            <div className="flex-1 min-h-0 flex flex-col gap-0.5 w-full items-center overflow-y-auto">
              {routes.map((route) => {
                const isActive = pathname.startsWith(route.path);
                const Icon = route.icon;
                return (
                  <button
                    key={route.path}
                    onClick={route.onClick || (() => navigate(route.path))}
                    title={route.name}
                    className="w-full flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl transition-all cursor-pointer"
                    style={{backgroundColor: isActive ? "rgba(255,255,255,0.12)" : "transparent"}}
                  >
                    {route.path === "/chat" ? (
                      <div className="relative">
                        <Icon
                          className="w-5 h-5 text-white transition-opacity"
                          style={{opacity: isActive ? 1 : 0.45}}
                        />
                        <UnreadBubble />
                      </div>
                    ) : (
                      <Icon className="w-5 h-5 text-white transition-opacity" style={{opacity: isActive ? 1 : 0.45}} />
                    )}
                    <span
                      className="text-[0.625rem] font-normal text-white leading-none text-center"
                      style={{opacity: isActive ? 1 : 0.45}}
                    >
                      {route.name}
                    </span>
                  </button>
                );
              })}

              {!routes.some((r) => r.path === "/chat") && (
                <button
                  onClick={() => navigate("/chat")}
                  title="BooksMatch Messages"
                  className="w-full flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl transition-all cursor-pointer relative"
                  style={{backgroundColor: pathname.startsWith("/chat") ? "rgba(255,255,255,0.12)" : "transparent"}}
                >
                  <div className="relative">
                    <MessageCircle
                      className="w-5 h-5 text-white transition-opacity"
                      style={{opacity: pathname.startsWith("/chat") ? 1 : 0.45}}
                    />
                    <UnreadBubble />
                  </div>
                  <span
                    className="text-[0.625rem] font-medium text-white leading-none"
                    style={{opacity: pathname.startsWith("/chat") ? 1 : 0.45}}
                  >
                    BooksMatch Messages
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content area */}
        <div className="h-full flex-1 min-w-0 flex flex-col overflow-hidden">
          <AppHeader routes={routes} />
          <div className="flex-1 min-h-0 overflow-hidden">
            <Outlet />
          </div>
        </div>
      </div>
    </ChatProvider>
  );
};

export default CommonAppLayout;
